import {
  feedbackcard,
  Field,
  FilteractionObject,
  FilterQuery
} from '@yes.technology/react-toolkit'
import { Form } from 'component'
import { resolveQuery } from 'filteraction/FilteractionResolver'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { FieldSettings } from 'shared/types'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { useSelectAndLink } from 'site/Renderer/components/SelectAndLink'
import { targetComponents } from 'site/Renderer/components/SelectAndLink/SelectAndLink.constants'
import { applySelectAndLinkValuesToGroupedFieldValues } from 'site/Renderer/components/SelectAndLink/utils/applySelectAndLinkValuesToGroupedFieldValues'
import { useSiteState } from 'siteState/shared'
import { toYesFilter } from '../../shared/toYesFilter/toYesFilter'
import { FieldValues, FilterAreaLayout, GroupedFieldValues } from '../../types'
import Section from '../Section/Section'
import useGroupedFieldValues from './useGroupedFieldValues'

type FilterFormProps = {
  elementUuid: string
  filterAreaLayout?: FilterAreaLayout
  initialGroupedFieldValues: GroupedFieldValues
  sections: Field[][]
  title?: string
  onSubmit?: (query: ReturnType<typeof toYesFilter>) => void
  uuidCurrentSitemodelObjectclass?: string
  defaultExecutedFilteraction?: string
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  style?: React.CSSProperties
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  filteractionOptions?: FilteractionObject[]
  dependsOnFilteractionOptions?: boolean
  clearFilterQuery?: (value: Partial<FilterQuery | undefined>) => void
  enableComposition?: boolean
  embedded?: boolean
  selectAndLinkTargetPath?: string
  hideFields?: boolean
}

const FilterForm = ({
  elementUuid,
  filterAreaLayout,
  initialGroupedFieldValues,
  sections,
  title,
  onSubmit,
  uuidCurrentSitemodelObjectclass,
  defaultExecutedFilteraction,
  navigationSliderBackgroundColor,
  navigationSliderColor,
  style,
  fieldSettings,
  concatFields,
  filteractionOptions,
  dependsOnFilteractionOptions,
  clearFilterQuery,
  enableComposition,
  embedded,
  selectAndLinkTargetPath,
  hideFields
}: FilterFormProps) => {
  const { getSession, closeSelectAndLink, onClearSelection } =
    useSelectAndLink()
  const selectAndLinkSession = getSession(targetComponents.FILTER_FIELDS)

  const { t, language } = useTranslation()

  const { groupedFieldValues, handleChange } = useGroupedFieldValues(
    initialGroupedFieldValues
  )

  const hasAppliedRef = useRef(false)

  useEffect(() => {
    const targetGroup = selectAndLinkSession?.targetGroup
    const targetField = selectAndLinkSession?.targetField
    const selectedOptions = selectAndLinkSession?.selectedOptions
    const isSessionCompleted = selectAndLinkSession?.isCompleted

    if (
      isSessionCompleted &&
      !hasAppliedRef.current &&
      selectAndLinkSession?.targetComponent ===
        targetComponents.FILTER_FIELDS &&
      targetGroup &&
      targetField &&
      Array.isArray(selectedOptions)
    ) {
      hasAppliedRef.current = true
      const nextGroupedFieldValues =
        applySelectAndLinkValuesToGroupedFieldValues(
          groupedFieldValues,
          targetGroup,
          targetField,
          selectedOptions
        )

      handleChange({
        type: 'set',
        payload: nextGroupedFieldValues
      })

      onClearSelection(targetComponents.FILTER_FIELDS)

      closeSelectAndLink()
    }
    if (!isSessionCompleted) {
      hasAppliedRef.current = false
    }
  }, [
    selectAndLinkSession,
    groupedFieldValues,
    handleChange,
    closeSelectAndLink,
    onClearSelection
  ])

  const [composition, handleComposition] = useState('1')

  const [_, setSiteFieldValues] =
    useSiteState<GroupedFieldValues>('field-values')

  const [, setSiteFieldComposition] = useSiteState<string>('field-composition')

  const handleClear = useCallback(() => {
    handleComposition('1')
    handleChange(null)
    clearFilterQuery?.(undefined)
    onClearSelection(targetComponents.FILTER_FIELDS)
  }, [clearFilterQuery, handleChange, onClearSelection])

  const submitFieldValues = useCallback(
    async (fieldValues: GroupedFieldValues, composition: string) => {
      const groupedValuesNoEmpty = removeEmptyValuesGrouped(fieldValues)

      const query = toYesFilter({
        groups: groupedValuesNoEmpty,
        composition,
        language
      })

      if (!Object.keys(query).length) {
        feedbackcard(t('generic-error'), {
          type: 'error',
          message: t('filter-fields.invalid-filter-action-format')
        })

        return
      }

      try {
        const resolvedQuery = await resolveQuery(
          query,
          console.log,
          [],
          true,
          true
        )

        resolvedQuery && onSubmit?.(resolvedQuery)
        resolvedQuery && setSiteFieldValues(groupedValuesNoEmpty)
        setSiteFieldComposition(composition)
      } catch (error) {
        console.error('Error fetching filteraction', error)

        feedbackcard(t('generic-error'), {
          type: 'error',
          message: t('filter-fields.invalid-filter-action-format')
        })
      }
    },
    [language, onSubmit, setSiteFieldValues, setSiteFieldComposition, t]
  )

  const onSubmitForm = (event: React.FormEvent) => {
    event.preventDefault()
    submitFieldValues(groupedFieldValues, composition)
  }

  const onFieldChange = useCallback(
    (state: Partial<GroupedFieldValues>) => {
      handleChange({
        type: 'merge',
        payload: state
      })
    },
    [handleChange]
  )

  return (
    <Form onSubmit={onSubmitForm} style={style}>
      <Section
        initialFieldValues={initialGroupedFieldValues[1]}
        handleChange={onFieldChange}
        executeFilter={submitFieldValues}
        {...{
          composition,
          sections,
          groupedFieldValues,
          handleClear,
          handleComposition,
          title,
          enableComposition,
          uuidCurrentSitemodelObjectclass,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          filterAreaLayout,
          defaultExecutedFilteraction,
          elementUuid,
          fieldSettings,
          concatFields,
          filteractionOptions,
          selectAndLinkTargetPath,
          dependsOnFilteractionOptions,
          embedded,
          hideFields
        }}
      />
    </Form>
  )
}

export function removeEmptyValues<T extends FieldValues>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, item]) =>
      Array.isArray(item.value) ? item.value.length > 0 : item.value
    )
  )
}

function removeEmptyValuesGrouped<T extends GroupedFieldValues>(
  groups: T
): GroupedFieldValues {
  return Object.fromEntries(
    Object.entries(groups).map(([group, values]) => [
      group,
      removeEmptyValues(values)
    ])
  )
}

export default FilterForm
